import React, { memo, useCallback } from 'react';
import { Box, Button, Heading, Text, useRecipe } from '@chakra-ui/react';
import { toggleRecipe } from '../../theme/recipes/toggle';
import { fieldFill, fieldFillHover } from '../../theme/themeColor';
import { RIFTBOUND_PRESS_ACTIVE } from './button';

export interface MultiToggleOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface MultiToggleGroupProps {
  label?: string;
  /** Toggle buttons render in this array order only; selection never reorders them. */
  options: MultiToggleOption[];
  /** Currently selected values. */
  selected: string[];
  /** Called with the toggled value — parent decides add/remove logic. */
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Whether buttons wrap to multiple lines. Default true. */
  wrap?: boolean;
  /** Attach buttons edge-to-edge with no gap. Default false. */
  attached?: boolean;
}

export const MultiToggleGroup = memo(function MultiToggleGroup({
  label,
  options,
  selected,
  onChange,
  disabled = false,
  wrap = true,
  attached = false,
}: MultiToggleGroupProps) {
  const toggle = useRecipe({ recipe: toggleRecipe });
  const handleClick = useCallback((value: string) => onChange(value), [onChange]);
  const tiled = wrap && !attached;
  const hasIcons = options.some((opt) => opt.icon);

  const getBorderRadius = useCallback(
    (index: number) => {
      if (!attached) return undefined;
      if (options.length === 1) return '4px';
      if (index === 0) return '4px 0 0 4px';
      if (index === options.length - 1) return '0 4px 4px 0';
      return '0';
    },
    [attached, options.length],
  );

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" w="100%" gap={2}>
      {label && (
        <Heading as="h3" size="sm">
          {label}
        </Heading>
      )}
      <Box
        w="100%"
        display="flex"
        flexWrap={wrap ? 'wrap' : 'nowrap'}
        gap={attached ? 0 : { base: tiled ? 2 : 1, md: 1 }}
        alignItems="stretch"
      >
        {options.map((opt, index) => {
          const isSelected = selected.includes(opt.value);
          const isDisabled = disabled || Boolean(opt.disabled);
          return (
            <Button
              key={opt.value}
              size="xs"
              variant={isSelected ? 'solid' : 'outline'}
              css={isSelected ? toggle({ tone: 'solid', selected: true }) : undefined}
              bg={isSelected ? undefined : fieldFill}
              color={isSelected ? undefined : 'text'}
              borderColor={isSelected ? undefined : 'border'}
              _hover={isSelected ? undefined : { bg: fieldFillHover, borderColor: 'accentMuted' }}
              opacity={opt.disabled ? 0.45 : undefined}
              onClick={() => {
                if (isDisabled) return;
                handleClick(opt.value);
              }}
              disabled={isDisabled}
              flexShrink={{ base: tiled ? 1 : 0, md: 0 }}
              flexGrow={{ base: tiled ? 1 : 0, md: 0 }}
              flexBasis={{ base: tiled ? 'calc((100% - 1rem) / 3)' : 'auto', md: 'auto' }}
              maxW={{ base: tiled ? 'calc((100% - 0.5rem) / 2)' : undefined, md: 'none' }}
              h="auto"
              minH={{ base: hasIcons ? '72px' : '44px', md: 'unset' }}
              minW={{ base: tiled ? 0 : '56px', md: '52px' }}
              py={{ base: 2, md: 2 }}
              px={{ base: 2, md: 1.5 }}
              WebkitTapHighlightColor="transparent"
              touchAction="manipulation"
              _active={RIFTBOUND_PRESS_ACTIVE}
              style={
                attached
                  ? { borderRadius: getBorderRadius(index), marginLeft: index === 0 ? 0 : '-1px' }
                  : undefined
              }
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
                w="100%"
              >
                {opt.icon}
                <Text
                  fontSize={{ base: 'xs', md: '2xs' }}
                  lineHeight="1.15"
                  textAlign="center"
                  fontWeight={isSelected ? 'semibold' : 'normal'}
                  textOverflow={{ base: 'unset', md: 'ellipsis' }}
                  overflow={{ base: 'visible', md: 'hidden' }}
                  whiteSpace={{ base: 'normal', md: 'nowrap' }}
                  maxW={{ base: '100%', md: '56px' }}
                >
                  {opt.label}
                </Text>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
});
